import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, Upload, ChevronLeft } from 'lucide-react';
import ClipartUploadDialog from './ClipartUploadDialog';
import { categoryAPI, photoAPI } from '../../services/api';

const ClipartSidebar = ({ isOpen, onClose, onClipartSelect }) => {
  const [cliparts, setCliparts] = useState([]);
  const [rootCategories, setRootCategories] = useState([]);
  const [childCategories, setChildCategories] = useState([]);
  const [selectedRootCategory, setSelectedRootCategory] = useState('all');
  const [selectedChildCategory, setSelectedChildCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  // Fetch cliparts with pagination
  const fetchCliparts = async (resetCliparts = true, pageNum = 1) => {
    if (resetCliparts) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const params = {
        page: pageNum,
        limit: 20,
      };

      // Add category filter
      if (selectedChildCategory !== 'all') {
        params.category_id = selectedChildCategory;
      } else if (selectedRootCategory && selectedRootCategory !== 'all') {
        params.category_id = selectedRootCategory;
      }

      // Add search filter
      if (searchQuery) {
        params.search = searchQuery;
      }

      // Use photoAPI to fetch clipart (stored in same table as photos)
      const resp = await photoAPI.getAll({ ...params, tags: 'clipart' });
      const data = resp.data;

      if (data.success) {
        const clipartsData = Array.isArray(data.data) ? data.data : data.data.photos || [];

        if (resetCliparts) {
          setCliparts(clipartsData);
        } else {
          setCliparts(prev => [...prev, ...clipartsData]);
        }

        setHasMore(clipartsData.length === params.limit);
        setPage(pageNum);
      }
    } catch (error) {
      console.error('Error fetching cliparts:', error);
      if (resetCliparts) {
        setCliparts([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Fetch root categories (parentId is null)
  const fetchRootCategories = async () => {
    try {
      const response = await categoryAPI.getAll({ type: 'clipart', parentId: '' });
      const data = response.data;

      if (data.success) {
        const rootCategoriesData = data.data || [];
        // Sort root categories by name
        rootCategoriesData.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase();
          const nameB = String(b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setRootCategories(rootCategoriesData);
        // Auto-select first root category if none selected
        if (!selectedRootCategory && rootCategoriesData.length > 0) {
          const firstRootId = rootCategoriesData[0].id;
          setSelectedRootCategory(firstRootId);
          // Also fetch child categories for the first root category
          fetchChildCategories(firstRootId);
        }
      }
    } catch (error) {
      console.error('Error fetching root categories:', error);
    }
  };

  // Fetch child categories for selected root category
  const fetchChildCategories = async (parentId) => {
    try {
      if (!parentId || parentId === 'all') {
        setChildCategories([]);
        return;
      }
      
      const response = await categoryAPI.getAll({ type: 'clipart', parentId });
      const data = response.data;

      if (data.success) {
        const childCategoriesData = data.data || [];
        // Sort child categories by name
        childCategoriesData.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase();
          const nameB = String(b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setChildCategories(childCategoriesData);
      }
    } catch (error) {
      console.error('Error fetching child categories:', error);
      setChildCategories([]);
    }
  };

  // Load more cliparts
  const loadMoreCliparts = () => {
    if (!loadingMore && hasMore) {
      fetchCliparts(false, page + 1);
    }
  };

  // Handle root category selection
  const handleRootCategorySelect = (category_id) => {
    setSelectedRootCategory(category_id);
    setSelectedChildCategory('all'); // Reset child category selection
    setPage(1); // Reset pagination

    // Fetch child categories for the selected root category
    if (category_id && category_id !== 'all') {
      fetchChildCategories(category_id);
    } else {
      setChildCategories([]);
    }
  };

  // Handle child category selection
  const handleChildCategorySelect = (category_id) => {
    setSelectedChildCategory(category_id);
    setPage(1); // Reset pagination
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(1); // Reset pagination
  };

  // Handle upload complete
  const handleUploadComplete = () => {
    fetchCliparts(); // Refresh cliparts list
  };

  // Effects
  useEffect(() => {
    if (isOpen) {
      fetchRootCategories();
      fetchCliparts();
    }
  }, [isOpen]);

  useEffect(() => {
    fetchCliparts();
  }, [selectedRootCategory, selectedChildCategory, searchQuery]);

  if (!isOpen) return null;

  return (
    <>
    <div className="fixed left-16 top-0 h-full w-80 bg-white border-r border-gray-200 shadow-lg z-500 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold">Clipart</h2>
        </div>
        <button
          onClick={() => setShowUploadDialog(true)}
          className="p-2 hover:bg-gray-100 rounded"
          title="Upload Clipart"
        >
          <Upload className="w-4 h-4" />
        </button>
      </div>

      {/* Search */}
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search clipart..."
            value={searchQuery}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
      </div>

      {/* Categories */}
      <div className="border-b border-gray-200 flex-shrink-0">
        {/* Root Categories Dropdown */}
        <div className="p-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
          <select
            value={selectedRootCategory || 'all'}
            onChange={(e) => handleRootCategorySelect(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
          >
            <option value="all">All Categories</option>
            {rootCategories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        {/* Child Categories Dropdown */}
        {childCategories.length > 0 && (
          <div className="px-3 pb-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">Subcategory</label>
            <select
              value={selectedChildCategory}
              onChange={(e) => handleChildCategorySelect(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
            >
              <option value="all">All Subcategories</option>
              {childCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Cliparts Grid */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="p-4">
            <div className="grid grid-cols-3 gap-2">
              {cliparts.map((clipart) => (
                <div
                  key={clipart.id}
                  onClick={() => onClipartSelect(clipart)}
                  className="cursor-pointer group"
                >
                  <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-2 group-hover:shadow-md transition-shadow flex items-center justify-center p-2">
                    {clipart.svgData ? (
                      <div
                        className="w-full h-full flex items-center justify-center"
                        dangerouslySetInnerHTML={{ __html: clipart.svgData }}
                      />
                    ) : clipart.thumbnailUrl ? (
                      <img
                        src={clipart.thumbnailUrl}
                        alt={clipart.name}
                        className="w-full h-full object-contain"
                        loading="lazy"
                      />
                    ) : clipart.storageUrl ? (
                      <img
                        src={clipart.storageUrl}
                        alt={clipart.name}
                        className="w-full h-full object-contain"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <span className="text-xs">No Preview</span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-xs font-medium text-gray-900 truncate">{clipart.name}</h3>
                  {clipart.isPremium && (
                    <span className="text-xs text-yellow-600">Premium</span>
                  )}
                </div>
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && cliparts.length > 0 && (
              <div className="mt-6 text-center">
                <button
                  onClick={loadMoreCliparts}
                  disabled={loadingMore}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 mx-auto"
                >
                  {loadingMore ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Loading...</span>
                    </>
                  ) : (
                    <span>Load More Clipart</span>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {!loading && cliparts.length === 0 && (
          <div className="text-center text-gray-500 mt-8">
            <p>No clipart found</p>
            {searchQuery && (
              <p className="text-sm mt-1">Try adjusting your search or category filter</p>
            )}
            {selectedChildCategory !== 'all' && (
              <p className="text-sm mt-1">Try selecting a different category</p>
            )}
          </div>
        )}
      </div>
    </div>

    {/* Clipart Upload Dialog */}
    <ClipartUploadDialog
      isOpen={showUploadDialog}
      onClose={() => setShowUploadDialog(false)}
      onUploadComplete={handleUploadComplete}
    />
    </>
  );
};

export default ClipartSidebar;
